import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ShoppingBag } from "lucide-react";
import { apiFetch } from "../services/api";
import { requireLogin } from "../utils/authHelpers";
import { CartContext } from "../contexts/CartContext";
import { AuthContext } from "../contexts/AuthContext";
import { ToastContext } from "../contexts/ToastContext";
import { PaymentContext } from "../contexts/PaymentContext";

export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    items,
    total,
    count,
    open,
    setOpen,
    updateItem,
    cartId,
    cartToken,
    refreshCart,
  } = useContext(CartContext);
  const { stripeEnabled } = useContext(PaymentContext);
  const toast = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const stripe = useStripe();
  const elements = useElements();
  const [showCheckout, setShowCheckout] = useState(false);
 const [showVisualizer, setShowVisualizer] = useState(false);
 const [visualizerResult, setVisualizerResult] = useState(null);
 const [itemImages, setItemImages] = useState({});

 useEffect(() => {
   if (!items.length) return;

   let isMounted = true;

   const loadImages = async () => {
     const results = await Promise.all(
       items.map(async (item) => {
         try {
           const res = await apiFetch(`/images/product/${item.product_id}`);
           if (!res.ok) return [item.product_id, null];
           const data = await res.json();
           return [item.product_id, data?.url || null];
         } catch {
           return [item.product_id, null];
         }
       })
     );

     if (isMounted) {
       setItemImages(Object.fromEntries(results));
     }
   };

   loadImages();

   return () => {
     isMounted = false;
   };
 }, [items]);

  const [visualizerPrompt, setVisualizerPrompt] = useState("");
  const [showVisualizerPayment, setShowVisualizerPayment] = useState(false);
  const [visualizerPaid, setVisualizerPaid] = useState(false);
  const [paying, setPaying] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [shipping, setShipping] = useState({
    full_name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postcode: "",
    country: "United Kingdom",
  });
  const closeCheckout = () => {
    if (paying) return;
    setShowCheckout(false);
  };
  const closeVisualizer = () => {
    setShowVisualizer(false);
  };
  // When cart drawer opens, support launching checkout from Virtual Try-On page
  useEffect(() => {
    if (!open) return;
    try {
      const flag = sessionStorage.getItem("virtualTryonLaunchCheckout");
      if (flag) {
        setShowCheckout(true);
        sessionStorage.removeItem("virtualTryonLaunchCheckout");
      }
    } catch (e) {
      // ignore
    }
  }, [open]);
const inputStyle = {
  padding: "14px 16px",
  border: "1px solid rgba(0,0,0,0.05)",
  borderRadius: 12,
  background: "#FBFAF8",
  fontSize: 14,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

  const payWithCard = async () => {
    if (!cartId) {
      toast("Unable to find your basket", "error");
      return;
    }

    setPaying(true);
    try {
      let data;

      if (stripeEnabled) {
        if (!stripe || !elements) {
          throw new Error("Payment form is not ready yet");
        }

        const intentRes = await apiFetch(
          "/checkout/intent",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ cart_id: cartId }),
          },
          { cartToken },
        );
        const intentData = await intentRes.json();
        if (!intentRes.ok) {
          throw new Error(intentData.error || "Unable to start payment");
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error("Card input is not available");
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(
          intentData.clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: shipping.full_name || "GardenStore Customer",
              },
            },
          },
        );

        if (error) {
          throw new Error(error.message || "Card payment failed");
        }

        if (!paymentIntent || paymentIntent.status !== "succeeded") {
          throw new Error("Payment was not completed");
        }

        const payload = {
          cart_id: cartId,
          shipping,
          payment_intent_id: paymentIntent.id,
        };

        const res = await apiFetch(
          "/checkout/complete",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          { cartToken },
        );
        data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || "Payment finalization failed");
        }
      } else {
        const fallbackRes = await apiFetch(
          "/checkout/card",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              cart_id: cartId,
              shipping,
              card: {
                name: shipping.full_name || "Guest Customer",
                number: "4242424242424242",
                exp_month: 12,
                exp_year: 2030,
                cvc: "123",
              },
            }),
          },
          { cartToken },
        );
        data = await fallbackRes.json();
        if (!fallbackRes.ok) {
          throw new Error(data.error || "Demo payment failed");
        }
      }

      setCheckoutResult(data);
      await refreshCart();
      toast(`Payment successful. Order #${data.order_id}`);
    } catch (e) {
      toast(e.message || "Payment failed", "error");
    } finally {
      setPaying(false);
    }
  };

  return (
    <>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.4)",
            zIndex: 900,
          }}
        />
      )}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          background: "#fff",
          zIndex: 901,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform .3s cubic-bezier(.4,0,.2,1)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 20px 24px",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
         <h2
  style={{
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "18px",
    fontWeight: 700,
    color: "#1a2e24",
    lineHeight: 1,
  }}
>
  <ShoppingBag
    size={22}
    strokeWidth={2}
    style={{
      color: "#1a2e24",
      flexShrink: 0,
    }}
  />
  <span>
    Shopping Bag <span style={{ color: "#6b7280" }}>({count})</span>
  </span>
</h2>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              color: "#5a7a68",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {items.length === 0 ? (
            <div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "80px 20px",
    gap: "16px",
  }}
>
  <div
    style={{
      width: "72px",
      height: "72px",
      borderRadius: "50%",
      background: "#FBFAF8",
      border: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <ShoppingBag
      size={34}
      strokeWidth={2}
      color="#6b7280"
    />
  </div>

  <p
    style={{
      margin: 0,
      fontSize: "15px",
      color: "#6b7280",
      fontWeight: 500,
    }}
  >
    Your bag is currently empty.
  </p>

<button
  onClick={() => navigate("/shop")}
  style={{
    background: "none",
    border: "none",
    color: "#C49A3C",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    cursor: "pointer",
  }}
>
  Start Shopping
</button>
</div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  gap: 14,
                  paddingBottom: 16,
                  marginBottom: 16,
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 10,
                    background: "#FBFAF8",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {itemImages[item.product_id] || item.image ? (
                    <img
                      src={itemImages[item.product_id] || item.image}
                      alt={item.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 10,
                      }}
                    />
                  ) : (
                    <span style={{ fontSize: 28 }}>👕</span>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#1a2e24",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.name}
                  </p>
                  <p
                    style={{
                      margin: "0 0 8px",
                      fontSize: 13,
                      color: "#1a6b45",
                      fontWeight: 700,
                    }}
                  >
                    £{parseFloat(item.sale_price || item.price).toFixed(2)}
                  </p>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <button
                      onClick={() => updateItem(item.id, item.qty - 1)}
                      style={{
                        width: 26,
                        height: 26,
                        border: "1px solid rgba(0,0,0,0.06)",
                        borderRadius: 6,
                        background: "#FBFAF8",
                        cursor: "pointer",
                        fontSize: 16,
                        color: "#1a6b45",
                      }}
                    >
                      −
                    </button>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        minWidth: 20,
                        textAlign: "center",
                      }}
                    >
                      {item.qty}
                    </span>
                    <button
                      onClick={() => updateItem(item.id, item.qty + 1)}
                      style={{
                        width: 26,
                        height: 26,
                        border: "1px solid rgba(0,0,0,0.06)",
                        borderRadius: 6,
                        background: "#FBFAF8",
                        cursor: "pointer",
                        fontSize: 16,
                        color: "#1a6b45",
                      }}
                    >
                      +
                    </button>
                    <button
                      onClick={() => updateItem(item.id, 0)}
                      style={{
                        marginLeft: "auto",
                        background: "none",
                        border: "none",
                        color: "#c0392b",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "#5a7a68", fontSize: 14 }}>Subtotal</span>
              <span style={{ fontWeight: 700, color: "#1a2e24" }}>
                £{total.toFixed(2)}
              </span>
            </div>
            <p style={{ fontSize: 12, color: "#7aaa90", margin: "0 0 14px" }}>
              {total >= 40
                ? "✓ Free delivery applied"
                : `Add £${(40 - total).toFixed(2)} for free delivery`}
            </p>
            {/* <button
              style={{
                width: "100%",
                background: "#1a6b45",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "14px",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => {
                setCheckoutResult(null);
                setShowCheckout(true);
              }}
            >
              Checkout — £{total.toFixed(2)}
            </button> */}
           <button
            onClick={() => {
              if (!requireLogin(user, navigate, toast, "Please sign in to use the Garden Visualizer experience.")) {
                return;
              }
              try {
                const payload = { items: items || [], visualizerPrompt: visualizerPrompt || "" };
                sessionStorage.setItem("virtualTryonPayload", JSON.stringify(payload));
              } catch (e) {
                console.warn("virtualTryon: unable to persist payload", e);
              }
              setVisualizerResult(null);
              setShowVisualizer(false);
              if (typeof setOpen === "function") {
                setOpen(false);
              }
              navigate("/virtual-tryon");
            }}
             onMouseEnter={(e) => {
    e.currentTarget.style.background = "#C49A3C";
    e.currentTarget.style.color = "#FFFFFF";
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.background = "#111110";
    e.currentTarget.style.color = "#FAFAF8";
  }}
            style={{
  width: "100%",
  height: "48px",           
  marginTop: "10px",
  background: "#111110",    
  color: "#FAFAF8",          
  border: "none",
  borderRadius: "0",        
  fontSize: "13px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "2px",      
  cursor: "pointer",
  transition: "all 0.25s ease",
}}
          >
             Garden Visualizer
          </button>
          </div>
        )}
      </div>


      {showCheckout && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 20,
          }}
          
        >
          
   
          <div
            style={{
              width: "min(640px, 100%)",
              maxHeight: "90vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                padding: "18px 24px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 20, color: "#1a2e24" }}>
                Secure Card Checkout
              </h3>
              <button
                onClick={closeCheckout}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 24,
                  color: "#5a7a68",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: 24, display: "grid", gap: 20 }}>
              {checkoutResult ? (
                <div
                  style={{
                    background: "#FBFAF8",
                    border: "1px solid rgba(0,0,0,0.06)",
                    borderRadius: 14,
                    padding: 18,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 8px",
                      color: "#1a6b45",
                      fontWeight: 700,
                    }}
                  >
                    Payment Approved
                  </p>
                  <p
                    style={{
                      margin: "0 0 6px",
                      color: "#1a2e24",
                      fontSize: 14,
                    }}
                  >
                    Order: #{checkoutResult.order_id}
                  </p>
                  <p
                    style={{
                      margin: "0 0 6px",
                      color: "#1a2e24",
                      fontSize: 14,
                    }}
                  >
                    Amount: £{Number(checkoutResult.total).toFixed(2)}
                  </p>
                  <p style={{ margin: 0, color: "#1a2e24", fontSize: 14 }}>
                    Card: •••• {checkoutResult.payment?.last4}
                  </p>
                  <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        setOpen(false);
                      }}
                      style={{
                        background: "#1a6b45",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "10px 16px",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <h4 style={{ margin: "0 0 10px", color: "#1a2e24" }}>
                      Shipping Details
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 10,
                      }}
                    >
                      <input
                        value={shipping.full_name}
                        onChange={(e) =>
                          setShipping((s) => ({
                            ...s,
                            full_name: e.target.value,
                          }))
                        }
                        placeholder="Full name"
                        style={{
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                      <input
                        value={shipping.postcode}
                        onChange={(e) =>
                          setShipping((s) => ({
                            ...s,
                            postcode: e.target.value,
                          }))
                        }
                        placeholder="Postcode"
                        style={{
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                      <input
                        value={shipping.line1}
                        onChange={(e) =>
                          setShipping((s) => ({ ...s, line1: e.target.value }))
                        }
                        placeholder="Address line 1"
                        style={{
                          gridColumn: "1 / -1",
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                      <input
                        value={shipping.line2}
                        onChange={(e) =>
                          setShipping((s) => ({ ...s, line2: e.target.value }))
                        }
                        placeholder="Address line 2 (optional)"
                        style={{
                          gridColumn: "1 / -1",
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                      <input
                        value={shipping.city}
                        onChange={(e) =>
                          setShipping((s) => ({ ...s, city: e.target.value }))
                        }
                        placeholder="City"
                        style={{
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                      <input
                        value={shipping.state}
                        onChange={(e) =>
                          setShipping((s) => ({ ...s, state: e.target.value }))
                        }
                        placeholder="County/State"
                        style={{
                          padding: "10px 12px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <h4 style={{ margin: "0 0 10px", color: "#1a2e24" }}>
                      Credit Card
                    </h4>
                    {stripeEnabled ? (
                      <div
                        style={{
                          padding: "12px 14px",
                          border: "1px solid rgba(0,0,0,0.05)",
                          borderRadius: 10,
                          background: "#fff",
                        }}
                      >
                        <CardElement options={{ hidePostalCode: true }} />
                      </div>
                    ) : (
                      <p
                        style={{
                          margin: "10px 0 0",
                          fontSize: 12,
                          color: "#5a7a68",
                        }}
                      >
                        Stripe is not configured. Checkout will run in demo card
                        mode until valid Stripe keys are set.
                      </p>
                    )}
                    <p
                      style={{
                        margin: "10px 0 0",
                        fontSize: 12,
                        color: "#7aaa90",
                      }}
                    >
                      Use test card 4242 4242 4242 4242 in Stripe test mode.
                    </p>
                  </div>

                  <div
                    style={{
                      background: "#FBFAF8",
                      border: "1px solid rgba(0,0,0,0.06)",
                      borderRadius: 12,
                      padding: 14,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                      }}
                    >
                      <span style={{ color: "#5a7a68" }}>Total</span>
                      <strong style={{ color: "#1a2e24" }}>
                        £{total.toFixed(2)}
                      </strong>
                    </div>
                    <button
                      onClick={payWithCard}
                      disabled={paying}
                      style={{
                        width: "100%",
                        marginTop: 8,
                        background: paying ? "#94b8a7" : "#1a6b45",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "12px 14px",
                        fontWeight: 700,
                        cursor: paying ? "default" : "pointer",
                      }}
                    >
                      {paying
                        ? "Processing..."
                        : stripeEnabled
                          ? `Pay £${total.toFixed(2)} by Card`
                          : `Pay £${total.toFixed(2)} (Demo Mode)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
    </>
  );
}
